import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./i18n";

// ============================================================================
// SENTRY INITIALIZATION - MUST BE BEFORE REACT RENDER
// ============================================================================

// Initialize Sentry for error monitoring and performance tracking
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const isProduction = import.meta.env.MODE === "production";

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: isProduction ? "production" : "development",
    enabled: isProduction, // Only enable in production

    // Performance Monitoring: sample 10% of transactions
    tracesSampleRate: 0.1,

    // Replay Integration: 10% of sessions, 100% of sessions with errors
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Integrations
    integrations: [
      // Browser tracing for monitoring performance
      Sentry.browserTracingIntegration(),
      // Session Replay for debugging errors
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // PII Filtering - Remove personally identifiable information
    beforeSend(event) {
      // Remove common PII patterns from message and extra data
      if (event.message) {
        event.message = sanitizePII(event.message);
      }

      // Clean extra data
      if (event.extra) {
        event.extra = Object.entries(event.extra).reduce((acc, [key, value]) => {
          acc[key] = sanitizePII(String(value));
          return acc;
        }, {} as Record<string, string>);
      }

      // Clean breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => ({
          ...breadcrumb,
          message: breadcrumb.message ? sanitizePII(breadcrumb.message) : undefined,
          data: breadcrumb.data ?
            Object.entries(breadcrumb.data).reduce((acc, [key, value]) => {
              acc[key] = sanitizePII(String(value));
              return acc;
            }, {} as Record<string, string>)
            : undefined,
        }));
      }

      return event;
    },
  });
}

// ============================================================================
// PII SANITIZATION HELPER
// ============================================================================

function sanitizePII(text: string): string {
  if (typeof text !== "string") return text;

  // Remove email addresses
  text = text.replace(/[\w.-]+@[\w.-]+\.\w+/g, "[EMAIL]");

  // Remove phone numbers (various formats)
  text = text.replace(/(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g, "[PHONE]");

  // Remove credit card patterns
  text = text.replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, "[CARD]");

  // Remove SSN/ID patterns
  text = text.replace(/\b\d{3}-\d{2}-\d{4}\b/g, "[SSN]");

  // Remove common name patterns (optional - be careful with false positives)
  // For now, skip this as it may be too aggressive

  return text;
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found");

// Clean up any legacy service workers from previous PWA setup
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((reg) => reg.unregister());
  });
}

// SPA deep-link recovery: restore path saved by 404.html fallback
const spaRedirect = sessionStorage.getItem("spa:redirect");
if (spaRedirect && spaRedirect !== "/" && spaRedirect !== window.location.pathname) {
  sessionStorage.removeItem("spa:redirect");
  window.history.replaceState(null, "", spaRedirect);
}

createRoot(rootEl).render(<App />);
